/**
 * AddressAutocomplete — debounced US address picker backed by Mapbox.
 *
 * Drop-in replacement for a bare `<Input>`. When the user picks a suggestion,
 * we call onSelect({ address, center: [lng, lat], accuracy }). Falling back
 * to onChange(text) on every keystroke so consumers can also use the raw
 * typed string (e.g. for free-form parcels Mapbox doesn't know).
 *
 * Usage:
 *   <AddressAutocomplete
 *     value={addr}
 *     onChange={setAddr}                        // raw text
 *     onSelect={({ address, center }) => ...}   // structured pick
 *     placeholder="Enter property address..."
 *   />
 */
import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { searchAddresses } from '@/lib/geocode';

export function AddressAutocomplete({
    value,
    onChange,
    onSelect,
    placeholder = 'Enter property address',
    disabled = false,
    className = '',
    inputClassName = '',
    dataTestId,
    autoFocus = false,
}) {
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const abortRef = useRef(null);
    const debounceRef = useRef(null);

    // Debounced fetch
    useEffect(() => {
        const q = String(value || '').trim();
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (abortRef.current) abortRef.current.abort();
        if (q.length < 3) { setResults([]); setLoading(false); return; }
        setLoading(true);
        debounceRef.current = setTimeout(async () => {
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            const r = await searchAddresses(q, { signal: ctrl.signal });
            if (!ctrl.signal.aborted) {
                setResults(r);
                setLoading(false);
                if (r.length > 0) setOpen(true);
            }
        }, 250);
        return () => clearTimeout(debounceRef.current);
    }, [value]);

    // Click-outside to close
    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const choose = (r) => {
        onChange?.(r.place_name);
        onSelect?.({ address: r.place_name, center: r.center, accuracy: r.accuracy, context: r.context });
        setOpen(false);
        setHighlight(-1);
        // Let parent control focus; defocus the input so the dropdown won't immediately reopen
        inputRef.current?.blur();
    };

    const onKeyDown = (e) => {
        if (!open || results.length === 0) {
            if (e.key === 'Enter' && value?.trim().length >= 3) {
                e.preventDefault();
                // Hand off raw text — caller decides what to do with non-Mapbox addresses
                onSelect?.({ address: value.trim(), center: null, accuracy: 'unknown' });
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlight >= 0) choose(results[highlight]);
            else choose(results[0]);
        } else if (e.key === 'Escape') {
            setOpen(false);
            setHighlight(-1);
        }
    };

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                <Input
                    ref={inputRef}
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange?.(e.target.value)}
                    onFocus={() => { if (results.length > 0) setOpen(true); }}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    spellCheck={false}
                    autoComplete="off"
                    className={`pl-9 ${inputClassName}`}
                    data-testid={dataTestId || 'address-autocomplete-input'}
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
                )}
            </div>

            {open && results.length > 0 && (
                <div
                    className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg ring-1 ring-black/5"
                    data-testid="address-autocomplete-suggestions"
                >
                    {results.map((r, i) => {
                        const isFirst = i === 0;
                        const isActive = i === highlight;
                        // Bold the matching prefix of the address line (best-effort)
                        return (
                            <button
                                key={r.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); choose(r); }}
                                onMouseEnter={() => setHighlight(i)}
                                className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 ${
                                    isActive ? 'bg-amber-50' : 'hover:bg-slate-50'
                                } ${isFirst ? '' : 'border-t border-slate-100'}`}
                                data-testid={`address-autocomplete-suggestion-${i}`}
                            >
                                <MapPin className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${isActive ? 'text-amber-600' : 'text-slate-400'}`} />
                                <span className="flex-1 min-w-0">
                                    <span className="block truncate text-slate-900">{(r.context?.region || r.context?.place) ? r.place_name.replace(/, United States$/, '') : r.place_name}</span>
                                    {r.accuracy && (
                                        <span className="text-[10px] text-slate-400 uppercase tracking-wide">{r.accuracy}</span>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
